import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Collapse,
  Avatar,
  FormControl,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CallSplit as BranchIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { BranchData, CompanyFormData } from "../../types/company-types";
import { RootState } from "../../../../store/Index";
import {
  FieldLabel,
  StyledSelect,
  StyledTextField,
} from "../../types/company-form";
import { MuiTelInput, MuiTelInputInfo } from "mui-tel-input";
// Add imports at top

interface Step2BranchesProps {
  formData: CompanyFormData;
  setFormData: React.Dispatch<React.SetStateAction<CompanyFormData>>;
}

function generateId() {
  return Math.random().toString(36).slice(2, 9);
}

const BRANCH_COLORS = [
  "#7C3AED",
  "#2563EB",
  "#059669",
  "#D97706",
  "#DC2626",
  "#0891B2",
];

function branchColor(index: number) {
  return BRANCH_COLORS[index % BRANCH_COLORS.length];
}

function getInitials(name: string) {
  if (!name.trim()) return "B";
  return name
    .trim()
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

// REPLACE BranchCard signature with:
// REPLACE the BranchCard props and internal logic:
function BranchCard({
  branch: initialBranch,
  index,
  onChange,
  onRemove,
  color100,
  color300,
  color400,
  color700,
  color800,
  isNew,
  onSave,
}: {
  branch: BranchData;
  index: number;
  onChange: (updated: BranchData) => void;
  onRemove: () => void;
  color100?: string;
  color300?: string;
  color400?: string;
  color700?: string;
  color800?: string;
  isNew?: boolean;
  onSave?: (branch: BranchData) => void;
}) {
  const [expanded, setExpanded] = useState(isNew ?? false);
  const [localBranch, setLocalBranch] = useState(initialBranch);
  const currentBranch = isNew ? localBranch : initialBranch;
  const color = branchColor(index);
  const cities: string[] =
    useSelector((state: RootState) => state.theme.cities) ?? [];
  const handlePhoneChange = (value: string, _info: MuiTelInputInfo) => {
    void _info;
    if (isNew) setLocalBranch((prev) => ({ ...prev, phone: value }));
    else onChange({ ...initialBranch, phone: value });
  };

  const set =
    (field: keyof BranchData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isNew)
        setLocalBranch((prev) => ({ ...prev, [field]: e.target.value }));
      else onChange({ ...initialBranch, [field]: e.target.value });
    };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
      {/* Card header */}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.25,
          bgcolor: "action.hover",
          cursor: "pointer",
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: color,
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {getInitials(currentBranch.name)}
        </Avatar>
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
          {currentBranch.name || (isNew ? "New Branch" : `Branch ${index + 1}`)}
        </Typography>
        {currentBranch.city && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
            {currentBranch.city}
          </Typography>
        )}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          sx={{ color: "error.main" }}
        >
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
        {expanded ? (
          <ExpandLessIcon sx={{ fontSize: 18, color: "text.disabled" }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 18, color: "text.disabled" }} />
        )}
      </Box>

      {/* Expandable fields */}

      <Collapse in={expanded}>
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <FieldLabel textcolor={color100}>
                Branch Name <span className="required">*</span>
              </FieldLabel>
              <StyledTextField
                placeholder="e.g. Harare Branch"
                value={currentBranch.name}
                onChange={set("name")}
                inputbg={color800}
                textcolor={color700}
                placeholdercolor={color300}
              />
            </Box>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <FieldLabel textcolor={color100}>Phone</FieldLabel>
              <MuiTelInput
                value={currentBranch.phone}
                onChange={handlePhoneChange}
                defaultCountry="ZW"
                variant="outlined"
                MenuProps={{
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                  disablePortal: true,
                  PaperProps: { sx: { maxHeight: 250, mt: 1 } },
                }}
                sx={{
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: color800,
                    borderRadius: "30px",
                    "& fieldset": { border: "none" },
                    "&:hover fieldset": { border: "none" },
                    fontSize: "13px",
                    color: color700,
                  },
                  "& .MuiInputBase-input": {
                    padding: "10px 14px",
                    fontSize: "13px",
                    color: color700,
                  },
                }}
              />
            </Box>
            <Box>
              <FieldLabel textcolor={color100}>Email</FieldLabel>
              <StyledTextField
                placeholder="branch@company.com"
                value={currentBranch.email}
                onChange={set("email")}
                inputbg={color800}
                textcolor={color700}
                placeholdercolor={color300}
              />
            </Box>
          </Box>

          <Box>
            <FieldLabel textcolor={color100}>Address</FieldLabel>
            <StyledTextField
              placeholder="Street address"
              value={currentBranch.address}
              onChange={set("address")}
              inputbg={color800}
              textcolor={color700}
              placeholdercolor={color300}
            />
          </Box>
          <Box>
            <FieldLabel textcolor={color100}>City</FieldLabel>
            <FormControl fullWidth sx={{ maxWidth: 320 }}>
              <StyledSelect
                value={currentBranch.city}
                displayEmpty
                selectbg={color800}
                textcolor={color700}
                onChange={(e) => {
                  const value = String(e.target.value);
                  if (isNew)
                    setLocalBranch((prev) => ({ ...prev, city: value }));
                  else onChange({ ...initialBranch, city: value });
                }}
                renderValue={(selected) =>
                  selected ? (
                    <span style={{ color: color700 }}>
                      {selected as string}
                    </span>
                  ) : (
                    <span style={{ color: color300 }}>Select city...</span>
                  )
                }
                MenuProps={{
                  PaperProps: { sx: { maxHeight: 280 } },
                }}
              >
                <MenuItem value="">
                  <em>Select city</em>
                </MenuItem>
                {cities.map((city) => (
                  <MenuItem key={city} value={city}>
                    {city}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </Box>
        </Box>

        {isNew && (
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", px: 2, pb: 2 }}
          >
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                if (!localBranch.name.trim()) return;
                onSave?.(localBranch);
                setLocalBranch({
                  id: "new",
                  name: "",

                  phone: "",
                  email: "",
                  city: "",
                  address: "",
                });
              }}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                bgcolor: color400,
                "&:hover": { bgcolor: color400 },
              }}
            >
              Add Branch
            </Button>
          </Box>
        )}
      </Collapse>
    </Paper>
  );
}

// REPLACE the two reduxTheme blocks with one:
export default function Branches({
  formData,
  setFormData,
}: Step2BranchesProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const color100 = reduxTheme.primary_colors?.[100];
  const color300 = reduxTheme.primary_colors?.[300];
  const color400 = reduxTheme.primary_colors?.[400] ?? "#8B4513";
  const color700 = reduxTheme.primary_colors?.[700];
  const color800 = reduxTheme.primary_colors?.[800];

  const [showForm, setShowForm] = useState(false);

  const updateBranch = (id: string, updated: BranchData) => {
    setFormData((prev) => ({
      ...prev,
      branches: prev.branches.map((b) => (b.id === id ? updated : b)),
    }));
  };

  const removeBranch = (id: string) => {
    setFormData((prev) => ({
      ...prev,
      branches: prev.branches.filter((b) => b.id !== id),
    }));
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
            Branches
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Add the company branch offices or locations.
          </Typography>
        </Box>
        {(formData.branches.length > 0 || showForm) && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 15 }} />}
            onClick={() => setShowForm(true)}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontSize: 13,
              borderColor: color400,
              color: color400,
              "&:hover": { borderColor: color400, bgcolor: `${color400}10` },
            }}
          >
            Add Branch
          </Button>
        )}
      </Box>

      {/* Empty state */}
      {formData.branches.length === 0 && !showForm && (
        <Box
          sx={{
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 3,
            py: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
            color: "text.disabled",
          }}
        >
          <BranchIcon sx={{ fontSize: 36 }} />
          <Typography variant="body2" fontWeight={500}>
            No branches added yet
          </Typography>
          <Typography variant="caption">
            Click Add Branch to add the first one.
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setShowForm(true)}
            sx={{
              mt: 1,
              textTransform: "none",
              borderRadius: 2,
              bgcolor: color400,
              "&:hover": { bgcolor: color400 },
            }}
          >
            Add Branch
          </Button>
        </Box>
      )}

      {/* New branch form */}
      {showForm && (
        <BranchCard
          key="new-branch-form"
          branch={{
            id: "new",
            name: "",

            phone: "",
            email: "",
            city: "",
            address: "",
          }}
          index={formData.branches.length}
          onChange={() => {}}
          onRemove={() => {}}
          color100={color100}
          color300={color300}
          color400={color400}
          color700={color700}
          color800={color800}
          isNew
          onSave={(branch) => {
            setFormData((prev) => ({
              ...prev,
              branches: [...prev.branches, { ...branch, id: generateId() }],
            }));
            setShowForm(false);
          }}
        />
      )}

      {/* Saved branches — collapsed */}
      {formData.branches.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2 }}>
          {[...formData.branches].reverse().map((branch, index) => (
            <BranchCard
              key={branch.id}
              branch={branch}
              index={formData.branches.length - 1 - index}
              onChange={(updated) => updateBranch(branch.id, updated)}
              onRemove={() => removeBranch(branch.id)}
              color100={color100}
              color300={color300}
              color700={color700}
              color800={color800}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
